import { useAuth } from "@/context/AuthContext";
import { Clock, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export function TrialBanner() {
  const { trialInfo, isAuthenticated, user } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();

  // Don't show if not authenticated, dismissed, or no trial info
  if (!isAuthenticated || isDismissed || !trialInfo || user?.is_admin) return null;

  // Don't show if subscription is active (not trial)
  if (trialInfo.subscriptionStatus === "active") return null;

  // Show expired state
  if (trialInfo.subscriptionStatus === "expired" || (!trialInfo.isTrialActive && trialInfo.subscriptionStatus === "trial")) {
    return (
      <div 
        className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-red-600 to-red-500 text-white py-2.5 px-4 shadow-lg"
        data-testid="trial-expired-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 animate-pulse" />
            <span className="font-medium text-sm md:text-base">
              Your trial has ended! Upgrade now for continued access
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/subscription")}
              className="px-4 py-1.5 bg-white text-red-600 rounded-full text-sm font-semibold hover:bg-red-50 transition-colors"
              data-testid="upgrade-now-btn"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show trial banner with days remaining
  const daysText = trialInfo.daysRemaining === 1 ? "1 day" : `${trialInfo.daysRemaining} days`;

  // Different styling based on urgency
  const isUrgent = trialInfo.daysRemaining <= 7;
  const bgClass = isUrgent 
    ? "bg-gradient-to-r from-orange-500 to-amber-500" 
    : "bg-gradient-to-r from-[#1E3A5F] to-[#2A4A6F]";

  return (
    <div 
      className={`fixed top-0 left-0 right-0 z-50 ${bgClass} text-white py-2.5 px-4 shadow-lg`}
      data-testid="trial-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20">
            <Sparkles className="h-4 w-4" />
          </div>
          <span className="font-medium text-sm md:text-base">
            {isUrgent ? (
              <>You have <span className="font-bold">{daysText}</span> left in your free trial!</>
            ) : (
              <>Enjoying free trial! <span className="font-bold">{daysText}</span> remaining</>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Hide upgrade button for pilot - will enable later */}
          {/* <button
            onClick={() => navigate("/subscription")}
            className="px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-sm font-medium transition-colors hidden md:block"
            data-testid="see-plans-btn"
          >
            View Plans
          </button> */}
          <button
            onClick={() => setIsDismissed(true)}
            className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close"
            data-testid="dismiss-banner-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
